/**
 * Subtle "Trusted by" strip of up to 3 rotating sponsor logos + a small
 * "Become a sponsor →" CTA that always shows so buyers can find the pitch.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchActiveSponsors, trackSponsorClick } from "@/lib/api";

export default function SponsorStrip() {
  const [sponsors, setSponsors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    fetchActiveSponsors()
      .then((d) => setSponsors(d.sponsors || []))
      .catch(() => setSponsors([]))
      .finally(() => setLoaded(true));
  }, []);

  // While loading, render nothing to avoid layout flash
  if (!loaded) return null;

  const handleClick = async (e, s) => {
    e.preventDefault();
    try {
      const { url } = await trackSponsorClick(s.sponsor_id);
      window.open(url || s.website_url, "_blank", "noopener,noreferrer");
    } catch {
      window.open(s.website_url, "_blank", "noopener,noreferrer");
    }
  };

  const hasSponsors = sponsors.length > 0;
  const slotsLeft = Math.max(0, 3 - sponsors.length);

  return (
    <div data-testid="sponsor-strip" className="mt-8 pt-6 border-t border-[#E5E2DC]">
      <div className="flex items-center gap-6 flex-wrap">
        <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold">
          {hasSponsors ? "Trusted by" : "Sponsor slots"}
        </span>
        {sponsors.map((s) => (
          <a
            key={s.sponsor_id}
            data-testid={`sponsor-${s.sponsor_id}`}
            href={s.website_url}
            onClick={(e) => handleClick(e, s)}
            className="inline-flex items-center gap-2 text-sm text-[#1C2B23] hover:text-[#2B5339] transition-colors"
            title={s.tagline || s.display_name}
          >
            {s.logo_url ? (
              <img
                src={s.logo_url}
                alt={s.display_name}
                className="h-6 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              />
            ) : (
              <span className="font-medium">{s.display_name}</span>
            )}
          </a>
        ))}
        {/* CTA always visible, so first buyers can find the pitch */}
        <Link
          to="/pricing"
          data-testid="become-a-sponsor"
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#2B5339] hover:text-[#1E3B28] font-medium group"
        >
          {hasSponsors
            ? `${slotsLeft} ${slotsLeft === 1 ? "slot" : "slots"} available · Become a sponsor`
            : "Become a sponsor · $199/mo"}
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  );
}
